import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatAgeSeconds } from './NeighborDataStatus';

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

const fmtCost = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2);
};

const neighborTableSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    px: 0.75,
    py: 0.5,
    fontSize: 12,
    verticalAlign: 'top',
  },
};

const rejectCellSx = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: 1.35,
  maxWidth: 0, // allow fixed-layout column to shrink and wrap
};

const macCellSx = {
  fontFamily: 'monospace',
  fontSize: 11,
  whiteSpace: 'nowrap',
};

const macHeaderSx = {
  width: '17ch',
};

const serialCellSx = {
  fontFamily: 'monospace',
  fontSize: 11,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '12ch',
};

const serialHeaderSx = {
  width: '12ch',
  minWidth: 100,
};

const candidateColSx = {
  width: '6%',
  pr: 2,
};

const attachColSx = {
  width: '8%',
  pl: 0.5,
};

const HeaderTip = ({ title, children }) => (
  <Tooltip title={title} enterDelay={400}>
    <Box component="span" sx={{ borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help' }}>
      {children}
    </Box>
  </Tooltip>
);

const resolveNeighborSlotChannel = (obs, allDevices, graphCandidates) => {
  const fromBackend = obs?.parent_slot_channel;
  if (fromBackend != null && fromBackend !== '' && Number(fromBackend) > 0) {
    return { value: Number(fromBackend), source: 'snapshot' };
  }

  const canInfer = Boolean(obs?.used_as_candidate || obs?.attach_method);
  if (!canInfer) {
    return { value: null, source: null };
  }

  const treeId = String(obs?.parent_tree_id || '').toLowerCase();
  if (!treeId) return { value: null, source: null };
  const parentSerial = String(obs?.neighbor_serial || '').toLowerCase();
  const parentDev = (allDevices || []).find(
    (d) => String(d?.serial || '').toLowerCase() === parentSerial,
  );
  if (parentDev) {
    const radio = (parentDev.radios || []).find(
      (r) => String(r?.name || '').toLowerCase() === treeId,
    );
    if (radio?.channel > 0) return { value: radio.channel, source: 'inferred' };
    const tm = (parentDev.tree?.treemgmt || []).find(
      (t) => String(t?.tree_id || '').toLowerCase() === treeId,
    );
    if (tm?.downlink_channel > 0) return { value: tm.downlink_channel, source: 'inferred' };
  }
  const cand = (graphCandidates || []).find(
    (c) => String(c?.parent_serial || '').toLowerCase() === parentSerial
      && String(c?.parent_tree_id || '').toLowerCase() === treeId,
  );
  if (cand?.parent_slot_channel > 0) {
    return { value: cand.parent_slot_channel, source: 'inferred' };
  }
  return { value: null, source: null };
};

const slotChannelTooltip = (slotCh) => {
  if (slotCh?.source === 'snapshot') {
    return 'Parent AP slot channel at job start (optimize input snapshot)';
  }
  if (slotCh?.source === 'inferred') {
    return 'Inferred from current device catalog (may differ from job snapshot)';
  }
  return '';
};

const JsonBlock = ({ data, maxHeight = 420 }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: 1.5,
      bgcolor: 'action.hover',
      borderRadius: 1,
      fontSize: 11,
      fontFamily: 'monospace',
      overflow: 'auto',
      maxHeight,
    }}
  >
    {JSON.stringify(data, null, 2)}
  </Box>
);

const AcsUsageGuide = ({ maxAgeSeconds }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
      How channel planning (DSATUR) uses ACS
    </Typography>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
      Mesh <strong>conflicts</strong> (GPS proximity + neighbour evidence) decide which APs need a new channel and
      which candidates are hard-rejected. ACS is a <strong>soft penalty</strong> on top — it nudges the picker toward
      cleaner spectrum on <strong>this device&apos;s</strong> survey; it does not replace conflict scoring.
    </Typography>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
      DSATUR scores <strong>all regulatory-allowed channels</strong> in the band, not only rows below.
      Channels with no fresh ACS sample get <strong>zero ACS penalty</strong> (neutral). Device fields{' '}
      <Box component="span" sx={{ fontFamily: 'monospace' }}>grade</Box> /{' '}
      <Box component="span" sx={{ fontFamily: 'monospace' }}>chan_eff</Box> /{' '}
      <Box component="span" sx={{ fontFamily: 'monospace' }}>ch_avail_pct</Box> are stored but{' '}
      <strong>not used</strong> in scoring today.
    </Typography>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
      Freshness: surveys older than {formatAgeSeconds(maxAgeSeconds)} are <strong>ignored</strong> for channel
      scoring (unlike golden neighbour scans, which stay last-known-good for 7 days).
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        bgcolor: 'action.hover',
        borderRadius: 1,
        fontSize: 11,
        fontFamily: 'monospace',
        overflow: 'auto',
      }}
    >
      {`per-channel penalty (subtracted from DSATUR score, max 15.0):
  busy:     (busy_pct / 100) × 12
  noise:    0 if NF ≤ −90 dBm; ramps to 4.0 by −70 dBm
  ext_bss:  min(ext_bss_count × 1.0, 3.0)
  radar:    +2.0 if true
HT40/80: worst penalty across 20 MHz slots in the bonded block`}
    </Box>
  </Box>
);

const AcsIndexedSamplesTable = ({ samples }) => {
  if (!samples?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        No channels in the fresh ACS index for this device.
      </Typography>
    );
  }
  return (
    <Table size="small" sx={{ mb: 2 }}>
      <TableHead>
        <TableRow>
          <TableCell>Band</TableCell>
          <TableCell>Ch</TableCell>
          <TableCell>Busy %</TableCell>
          <TableCell>NF dBm</TableCell>
          <TableCell>Ext BSS</TableCell>
          <TableCell>Radar</TableCell>
          <TableCell>Ch avail %</TableCell>
          <TableCell>Penalty</TableCell>
          <TableCell>Components</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {samples.map((s) => (
          <TableRow key={`${s.band}-${s.channel}`}>
            <TableCell>{s.band}</TableCell>
            <TableCell>{s.channel}</TableCell>
            <TableCell>{s.busy_pct}</TableCell>
            <TableCell>{s.noise_floor_dbm}</TableCell>
            <TableCell>{s.ext_bss_count}</TableCell>
            <TableCell>{s.radar ? 'yes' : 'no'}</TableCell>
            <TableCell sx={{ color: 'text.secondary' }} title="Informational only — not used in DSATUR score">
              {s.ch_avail_pct ?? '—'}
            </TableCell>
            <TableCell>{s.computed_penalty}</TableCell>
            <TableCell sx={{ fontSize: 11, fontFamily: 'monospace' }}>
              b:{s.busy_penalty_component} n:{s.noise_penalty_component} e:{s.ext_bss_penalty_component} r:{s.radar_penalty_component}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const AcsChannelScoringTable = ({ rows }) => {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        No AP radios on this device for channel scoring.
      </Typography>
    );
  }
  return (
    <>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Penalty applied if DSATUR evaluates staying on (or moving to) the current channel at the configured plan
        bandwidth. Other allowed channels get the same formula when scored; only the current channel is shown here.
      </Typography>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell>Radio</TableCell>
            <TableCell>Band</TableCell>
            <TableCell>Tree</TableCell>
            <TableCell>Current ch</TableCell>
            <TableCell>Plan BW</TableCell>
            <TableCell>ACS penalty</TableCell>
            <TableCell>Sample?</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.radio_name}-${r.band}-${r.tree_id}`}>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.radio_name}</TableCell>
              <TableCell>{r.band}</TableCell>
              <TableCell>{r.tree_id || '—'}</TableCell>
              <TableCell>{r.current_channel}</TableCell>
              <TableCell>{r.channel_bandwidth_mhz} MHz</TableCell>
              <TableCell>{r.current_channel_penalty}</TableCell>
              <TableCell>{r.current_sample_found ? 'yes' : 'no ACS row'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

const InputDebugPanel = ({ inputDebug }) => {
  const [serial, setSerial] = useState('');

  const devices = useMemo(
    () => inputDebug?.devices ?? [],
    [inputDebug?.devices],
  );
  const selected = useMemo(
    () => devices.find((d) => d.serial === serial) || devices[0] || null,
    [devices, serial],
  );

  if (!inputDebug) return null;

  const warnings = inputDebug.warnings || [];
  const summary = inputDebug.site_summary || {};
  const neighborFreshness = inputDebug.neighbor_freshness || {};
  const staleThresholdS = neighborFreshness.stale_threshold_s;
  const staleCount = neighborFreshness.stale_count || 0;
  const acsSummary = inputDebug.acs_summary || {};
  const acsStaleCount = acsSummary.stale || 0;
  const acsMaxAgeS = acsSummary.max_age_s;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Input audit (debug)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Snapshot of every input the optimize planner read at job start — device fields, golden neighbor RSSI (STA/AP),
        ACS samples used for DSATUR scoring, and graph parent candidates.
      </Typography>

      {warnings.length === 0 ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          No input warnings — all checked fields present for enabled stages.
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warnings.length} input warning(s) — review before trusting the plan.
        </Alert>
      )}

      {staleCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {staleCount === 1 ? '1 Node has a stale golden neighbour scan' : `${staleCount} Nodes have stale golden neighbour scans`}
          {' '}(older than {formatAgeSeconds(staleThresholdS)}).
          {' '}These reports are <strong>still used</strong> as the last known good snapshot for topology,
          power, and channel — staleness is a warning only, not a block. Re-scan if RF may have changed.
          {neighborFreshness.oldest_serial ? (
            <> Oldest: <Box component="span" sx={{ fontFamily: 'monospace' }}>{neighborFreshness.oldest_serial}</Box>
              {neighborFreshness.oldest_age_s != null ? ` (${formatAgeSeconds(neighborFreshness.oldest_age_s)})` : ''}.</>
          ) : null}
        </Alert>
      )}

      {acsStaleCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {acsStaleCount === 1 ? '1 device has a stale ACS survey' : `${acsStaleCount} devices have stale ACS surveys`}
          {' '}(older than {formatAgeSeconds(acsMaxAgeS)}).
          {' '}Stale ACS is <strong>not used</strong> for DSATUR channel penalties — planning falls back to
          conflict-only scoring for those devices. Re-run an ACS survey to influence channel picks.
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip size="small" label={`Devices: ${devices.length}`} />
        <Chip size="small" label={`Warnings: ${warnings.length}`} />
        {staleCount > 0 && (
          <Chip size="small" color="warning" variant="outlined" label={`Stale scans: ${staleCount}`} />
        )}
        <Chip size="small" label={`ACS fresh: ${summary.acs_fresh ?? 0}`} />
        {(summary.acs_stale ?? 0) > 0 && (
          <Chip size="small" color="warning" variant="outlined" label={`ACS stale: ${summary.acs_stale}`} />
        )}
        <Chip size="small" label={`ACS missing: ${summary.acs_missing ?? 0}`} />
        <Chip size="small" label={`Zero candidates: ${summary.zero_candidates ?? 0}`} />
      </Stack>

      <Accordion defaultExpanded={warnings.length > 0} disableGutters sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Warnings ({warnings.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {warnings.length === 0 ? (
            <Typography variant="body2" color="text.secondary">None</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Serial</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warnings.map((w, idx) => (
                  <TableRow key={`${w.code}-${w.serial}-${idx}`}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{fmt(w.serial)}</TableCell>
                    <TableCell>{w.code}</TableCell>
                    <TableCell>{fmt(w.section)}</TableCell>
                    <TableCell>{w.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion disableGutters sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Site summary &amp; gates</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Missing golden scans block optimize; stale scans (older than{' '}
            {formatAgeSeconds(staleThresholdS)}) do not — they are still read as last known good.
            {' '}ACS surveys stale after {formatAgeSeconds(acsMaxAgeS)} are omitted from channel scoring.
          </Typography>
          <JsonBlock data={{
            gates: inputDebug.gates,
            site_summary: summary,
            acs_summary: inputDebug.acs_summary,
            neighbor_freshness: inputDebug.neighbor_freshness,
            request: inputDebug.request,
          }} />
        </AccordionDetails>
      </Accordion>

      {devices.length > 0 && (
        <>
          <FormControl size="small" fullWidth sx={{ mb: 1, mt: 1 }}>
            <InputLabel id="input-debug-serial-label">Device</InputLabel>
            <Select
              labelId="input-debug-serial-label"
              label="Device"
              value={selected?.serial || ''}
              onChange={(e) => setSerial(e.target.value)}
            >
              {devices.map((d) => (
                <MenuItem key={d.serial} value={d.serial}>
                  {d.serial} {d.is_cap ? '(CAP)' : ''} {!d.connected ? '(offline)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selected && (
            <>
              <Accordion defaultExpanded disableGutters sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Neighbor scan &amp; graph candidates ({selected.graph?.candidate_count ?? 0})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Golden neighbour report — who this Node heard. Parent slot is resolved by{' '}
                    <strong>AP BSSID</strong> (scan <Box component="span" sx={{ fontFamily: 'monospace' }}>AP_BSSID</Box>)
                    matching the parent AP radio MAC when present; stale scan channels are ignored.
                    Wrong BSSID falls back to channel when it yields a unique open slot (
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>channel_fallback_after_bssid</Box>
                    ). Without BSSID, attach uses channel match only (
                    <Box component="span" sx={{ fontFamily: 'monospace' }}>channel_fallback</Box>
                    ). &quot;Candidate&quot; = passed graph parent
                    filters (managed, online, attach slot, RSSI floor). Reject reason explains rows marked no.
                    Slot ch with * = inferred from current device catalog, not job snapshot.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Source: {selected.neighbors?.source}
                    {selected.neighbors?.stale && selected.neighbors?.age_seconds != null ? (
                      <> · scan age {formatAgeSeconds(selected.neighbors.age_seconds)} (stale, still used as last known good)</>
                    ) : selected.neighbors?.age_seconds != null ? (
                      <> · scan age {formatAgeSeconds(selected.neighbors.age_seconds)}</>
                    ) : null}
                  </Typography>
                  {selected.neighbors?.observations?.length > 0 && (
                    <TableContainer sx={{ mb: 2, overflowX: 'auto', maxWidth: '100%' }}>
                      <Table size="small" sx={neighborTableSx}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={serialHeaderSx}>Neighbor</TableCell>
                            <TableCell sx={{ width: '4%' }}>Band</TableCell>
                            <TableCell sx={{ width: '3%' }}>
                              <HeaderTip title="Channel from the golden neighbor scan row (may be stale)">
                                Ch
                              </HeaderTip>
                            </TableCell>
                            <TableCell sx={{ width: '3%' }}>
                              <HeaderTip title="Parent AP slot channel at job start; client fallback only on rows with attach resolution">
                                Slot ch
                              </HeaderTip>
                            </TableCell>
                            <TableCell sx={macHeaderSx}>AP BSSID</TableCell>
                            <TableCell sx={{ width: '6%' }}>Slot</TableCell>
                            <TableCell sx={{ width: '4%' }}>
                              <HeaderTip title="Downlink: signal from parent AP received at this node's STA">
                                RSSI↓
                              </HeaderTip>
                            </TableCell>
                            <TableCell sx={{ width: '4%' }}>Adj.</TableCell>
                            <TableCell sx={{ width: '4%' }}>
                              <HeaderTip title="Uplink: signal from this node's STA received at parent AP">
                                RSSI↑
                              </HeaderTip>
                            </TableCell>
                            <TableCell sx={{ width: '5%' }}>AP SNR</TableCell>
                            <TableCell sx={{ width: '5%' }}>STA SNR</TableCell>
                            <TableCell sx={candidateColSx}>Candidate</TableCell>
                            <TableCell sx={attachColSx}>
                              <HeaderTip title="How attach-slot was resolved (e.g. ap_bssid, channel_fallback)">
                                Attach
                              </HeaderTip>
                            </TableCell>
                            <TableCell sx={{ width: '16%' }}>Reject</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selected.neighbors.observations.map((o, idx) => {
                            const rejectText = o.used_as_candidate
                              ? '—'
                              : (o.candidate_reject_detail || o.candidate_reject_code || '—');
                            const slotCh = resolveNeighborSlotChannel(
                              o,
                              devices,
                              selected.graph?.candidates,
                            );
                            const slotChTip = slotChannelTooltip(slotCh);
                            return (
                              <TableRow key={`${o.neighbor_serial}-${idx}`}>
                                <TableCell sx={serialCellSx}>
                                  <Tooltip title={o.neighbor_serial || ''} enterDelay={400}>
                                    <Box component="span">{o.neighbor_serial}</Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{o.band}</TableCell>
                                <TableCell>
                                  <Tooltip title="Channel from golden neighbor scan" enterDelay={400}>
                                    <span>{o.channel ?? '—'}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  {slotCh?.value != null ? (
                                    <Tooltip title={slotChTip} enterDelay={400}>
                                      <span>{slotCh.value}{slotCh.source === 'inferred' ? '*' : ''}</span>
                                    </Tooltip>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell sx={macCellSx}>
                                  <Tooltip title={fmt(o.ap_bssid)} enterDelay={400}>
                                    <Box component="span">{fmt(o.ap_bssid)}</Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{fmt(o.parent_tree_id)}</TableCell>
                                <TableCell>
                                  <Tooltip title="Downlink: parent AP → this node's STA" enterDelay={400}>
                                    <span>{o.rssi_down_dbm}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{o.rssi_down_adjusted_dbm ?? '—'}</TableCell>
                                <TableCell>
                                  <Tooltip title="Uplink: this node's STA → parent AP" enterDelay={400}>
                                    <span>{o.rssi_up_dbm}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{o.ap_snr}/{o.ap_nf_dbm}</TableCell>
                                <TableCell>{o.sta_snr}/{o.sta_nf_dbm}</TableCell>
                                <TableCell sx={candidateColSx}>{o.used_as_candidate ? 'yes' : 'no'}</TableCell>
                                <TableCell sx={attachColSx}>{fmt(o.attach_method)}</TableCell>
                                <TableCell sx={rejectCellSx}>
                                  {rejectText !== '—' ? (
                                    <Tooltip title={rejectText} enterDelay={400}>
                                      <Box component="span">{rejectText}</Box>
                                    </Tooltip>
                                  ) : (
                                    rejectText
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {selected.graph?.candidates?.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Graph candidates ({selected.graph.candidate_count})
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Parent links accepted by <Box component="span" sx={{ fontFamily: 'monospace' }}>BuildParentGraph</Box>
                        {' '}after attach-slot resolution (BSSID-first). Each row is one parent serial + slot pair.
                      </Typography>
                      <TableContainer sx={{ mb: 2, overflowX: 'auto', maxWidth: '100%' }}>
                        <Table size="small" sx={{ ...neighborTableSx, minWidth: 520 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={serialHeaderSx}>Parent</TableCell>
                              <TableCell sx={{ width: '6%' }}>Band</TableCell>
                              <TableCell sx={macHeaderSx}>AP BSSID</TableCell>
                              <TableCell sx={{ width: '8%' }}>Slot</TableCell>
                              <TableCell sx={{ width: '8%' }}>
                                <HeaderTip title="Downlink: signal from parent AP received at this node's STA">
                                  RSSI↓
                                </HeaderTip>
                              </TableCell>
                              <TableCell sx={{ width: '8%' }}>
                                <HeaderTip title="Uplink: signal from this node's STA received at parent AP">
                                  RSSI↑
                                </HeaderTip>
                              </TableCell>
                              <TableCell sx={{ width: '8%' }}>Cost</TableCell>
                              <TableCell sx={{ width: '6%' }}>Current</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selected.graph.candidates.map((c, idx) => (
                              <TableRow key={`${c.parent_serial}-${c.parent_tree_id}-${idx}`}>
                                <TableCell sx={serialCellSx}>
                                  <Box component="span">{c.parent_serial}</Box>
                                </TableCell>
                                <TableCell>{c.band}</TableCell>
                                <TableCell sx={macCellSx}>{fmt(c.parent_ap_bssid)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{fmt(c.parent_tree_id)}</TableCell>
                                <TableCell>
                                  <Tooltip title="Downlink: parent AP → this node's STA" enterDelay={400}>
                                    <span>{c.rssi_down_dbm}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="Uplink: this node's STA → parent AP" enterDelay={400}>
                                    <span>{c.rssi_up_dbm}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{fmtCost(c.cost)}</TableCell>
                                <TableCell>{c.is_current ? 'yes' : 'no'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                  <JsonBlock data={selected.graph} />
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    ACS survey &amp; channel scoring ({selected.acs?.status})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {selected.acs?.status === 'fresh' && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Fresh ACS (age {formatAgeSeconds(selected.acs.age_seconds)}, max{' '}
                      {formatAgeSeconds(selected.acs.max_age_seconds)}). Indexed samples below feed DSATUR penalties
                      for this device when it is recolored for mesh conflicts.
                    </Alert>
                  )}
                  {selected.acs?.status === 'stale' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      ACS survey age {formatAgeSeconds(selected.acs.age_seconds)} exceeds{' '}
                      {formatAgeSeconds(selected.acs.max_age_seconds)} — <strong>ignored</strong> for channel scoring on
                      this device. DSATUR uses conflict-only scoring until a new survey is ingested.
                    </Alert>
                  )}
                  {selected.acs?.status === 'missing' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      No ACS survey on file for this device — zero ACS penalty on all channel candidates (neutral, not
                      “good”).
                    </Alert>
                  )}
                  <AcsUsageGuide maxAgeSeconds={selected.acs?.max_age_seconds ?? acsMaxAgeS} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Indexed samples ({selected.acs?.indexed_samples?.length ?? 0})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Per-channel readings from this device&apos;s fresh ACS report. Penalty is subtracted when DSATUR
                    scores that channel as a candidate. Channels not listed were not scanned or survey is stale.
                  </Typography>
                  <AcsIndexedSamplesTable samples={selected.acs?.indexed_samples} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Current radio penalty ({selected.acs?.channel_scoring?.length ?? 0})
                  </Typography>
                  <AcsChannelScoringTable rows={selected.acs?.channel_scoring} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Raw JSON
                  </Typography>
                  <JsonBlock data={selected.acs} maxHeight={280} />
                </AccordionDetails>
              </Accordion>

              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Full device input</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <JsonBlock data={selected} />
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </>
      )}
    </Paper>
  );
};

export default InputDebugPanel;
